import React, { useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  Snackbar,
  TextField,
  Typography
} from '@mui/material';
import { WordmarkIcon } from '../../Media/WordmarkIcon';
import { getCookie, setCookieWithExpiration } from '../../utils';
import ForgotPasswordDialog from './ForgotPasswordDialog';

const AUTH_API_BASE =
  import.meta.env.VITE_SERVER_URL_V2 ||
  'https://apis-v2.residentboost.com/apis/v1';

const LoginPage: React.FC = () => {
  const history = useHistory();
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });

  // If already authenticated, redirect to home
  const existingToken = getCookie('rb_access_token');
  if (existingToken) {
    history.replace('/');
    return null;
  }

  const loginUser = async (): Promise<void> => {
    try {
      setLoading(true);
      const emailTrimmed = email.toLowerCase().trim();
      const res = await axios.post(
        AUTH_API_BASE + '/user/loginUser',
        { email: emailTrimmed, password: password.trim() },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (res?.data?.status === 'success') {
        setCookieWithExpiration(
          'rb_access_token',
          `Bearer ${res.data.data.token}`
        );
        // Return the user to where they were before the token expired, if known
        const redirectPath = sessionStorage.getItem('post_login_redirect');
        sessionStorage.removeItem('post_login_redirect');
        // Full page reload so Apollo client and providers reinitialize with the new token
        window.location.href = redirectPath || '/';
      } else {
        setSnackbar({
          open: true,
          message: 'Invalid email or password.',
          severity: 'error'
        });
      }
    } catch (error) {
      const errorMessage =
        axios.isAxiosError(error) && error.response?.data?.message
          ? error.response.data.message
          : 'An error occurred while logging in.';
      setSnackbar({ open: true, message: errorMessage, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && email && password && !loading) {
      loginUser();
    }
  };

  return (
    <Box
      sx={theme => ({
        backgroundColor: theme.palette.background.default,
        // A flat near-black fills the viewport with nothing. This puts a soft
        // brand-navy glow behind the card so it sits in a space instead of a
        // void; light mode keeps the plain page color.
        ...(theme.palette.mode === 'dark' && {
          backgroundImage:
            'radial-gradient(120% 80% at 50% 0%, #1B2033 0%, #11141E 60%)'
        }),
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: 2
      })}
    >
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ForgotPasswordDialog
        open={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
      />
      <Card
        sx={theme => ({
          minWidth: 400,
          maxWidth: 450,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          p: 4,
          borderRadius: 2,
          // Elevation shadows are black, so they do nothing on a dark page and
          // the card bleeds into it. Borders are how dark surfaces separate.
          ...(theme.palette.mode === 'dark' && {
            border: `1px solid ${theme.palette.divider}`
          })
        })}
      >
        <WordmarkIcon
          sx={{
            height: 40,
            margin: 'auto',
            // Brand navy disappears into the dark card, same reason
            // muiTheme swaps links/buttons to the light brand blue.
            color: theme =>
              theme.palette.mode === 'dark'
                ? theme.palette.primary.light
                : theme.palette.primary.main
          }}
        />
        <Typography variant="h5" sx={{ mb: 2 }}>
          Log in
        </Typography>
        <TextField
          label="Email"
          fullWidth
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your email..."
          size="small"
          required
          autoFocus
        />
        <TextField
          label="Password"
          fullWidth
          value={password}
          onChange={e => setPassword(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your password..."
          type="password"
          size="small"
          required
        />
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          disabled={!email || !password || loading}
          onClick={loginUser}
          // Brand navy on a dark card makes the page's one action its dullest
          // element. Same light-blue swap muiTheme applies to outlined/text
          // primary buttons, scoped so the disabled state keeps MUI's greying.
          sx={theme =>
            theme.palette.mode === 'dark'
              ? {
                  '&:not(.Mui-disabled)': {
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.background.default
                  },
                  '&:not(.Mui-disabled):hover': {
                    backgroundColor: alpha(theme.palette.primary.light, 0.85)
                  }
                }
              : {}
          }
        >
          {loading ? 'Logging in...' : 'Log in'}
        </Button>
        <Button variant="text" onClick={() => setShowForgotPassword(true)}>
          Forgot Password?
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Having trouble? Email us at support@getzuma.com
        </Typography>
      </Card>
    </Box>
  );
};

export default LoginPage;
